import to from 'await-to-js';
import axios from 'axios';
import i18n from '@/plugins/vue-i18n';
import { useAlertsStore } from '@/stores/alerts';
import { useSnackbarStore } from '@/stores/snackbar';
import { useToolDataStore } from '@/stores/tool-data';
import { filter, forEach, get, isEqual, size, some, sortBy, uniqBy } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { useEditableTableStore } from '@/stores/utils/editable-table-store-utils';
import handleErrorMessages from './utils/validation';

export const useReferenceClusteringStore = defineStore('reference-clustering', () => {
  const toolDataStore = useToolDataStore();
  const snackbarStore = useSnackbarStore();
  const alertsStore = useAlertsStore();

  const {
    // States
    localDataGetterName,
    localDataChangesPerCell,
    localTableRowContents,
    localInvalidErrors,
    // Getters
    getTableRows,
    hasInvalidValuesInAnyCell,
    userMadeChangesToAnyCell,
    hasDataIn,
    hasChangesInCell,
    hasInvalidDataInCell,
    // Actions
    setNameOfLocalGetter,
    setTableRows,
    setInputValue,
    setInputError,
    setCellChanges,
    unloadData,
    markAllRowsAsChanged,
    initialiseEditableTable,
    onValueChanged,
    setUploadedData,
  } = useEditableTableStore();

  // State
  const loading = shallowRef(false);
  const clusterSchemes = ref([]);
  const validClusterSchemes = ref([]);
  const clusterSchemeInformation = ref({});
  const isLoadingClusterInformation = shallowRef(false);
  const selectedScheme = ref(null);
  const selectableClusterNames = ref([]);
  const stores = ref([]);
  const storeKeys = ref(null);

  // Getters
  const getClusterSchemes = computed(() => {
    return sortBy(clusterSchemes.value, ['name']);
  });

  // Private helpers
  function refreshScheme(scheme) {
    const index = clusterSchemes.value.findIndex(obj => obj._id === scheme._id);
    Object.assign(clusterSchemes.value[index], scheme);
  }
  // Actions
  async function fetchClusterScheme() {
    loading.value = true;
    const [err, response] = await to(
      axios.get(`/api/reference/reference-cluster-schemes/${selectedScheme.value._id}`)
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    selectedScheme.value = response.data;
    refreshScheme(response.data);
  }
  async function updateClusters(updates) {
    // remove the unassigned group before saving
    const cleanUpdates = filter(updates, c => {
      return c.clusterName !== 'Unassigned';
    });
    const selectedClusterSchemeId = selectedScheme.value._id;
    const clusterCount = uniqBy(cleanUpdates, 'clusterName').length;
    loading.value = true;
    const [err] = await to(
      axios.patch(`/api/reference/reference-cluster-schemes/${selectedClusterSchemeId}/clusters`, {
        updates: cleanUpdates,
        clusterCount,
      })
    );
    loading.value = false;
    if (err) throw new Error(err.message);

    fetchClusterScheme();
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }

  async function updateClusterSchemeName(update) {
    loading.value = true;
    const [err] = await to(
      axios.patch(`/api/reference/reference-cluster-schemes/${update.clusterSchemeId}/name`, {
        name: update.name,
      })
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }

  async function fetchClusterSchemes() {
    loading.value = true;
    const [err, response] = await to(axios.get('/api/reference/reference-cluster-schemes'));
    loading.value = false;
    if (err) throw new Error(err.message);
    clusterSchemes.value = response.data;
    // TODO vuetify
    // dispatch('refreshSelectedScheme');
    return response.data;
  }

  async function deleteClusterScheme(clusterSchemeId) {
    loading.value = true;
    const [err] = await to(
      axios.delete(`/api/reference/reference-cluster-schemes/${clusterSchemeId}`)
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }

  async function saveClusteringSchemes(bodyData) {
    loading.value = true;
    const [err, response] = await to(
      axios.post('/api/reference/reference-cluster-schemes', bodyData)
    );
    const options = { displayEmpty: false };
    handleErrorMessages({ response, options });
    loading.value = false;

    if (err) {
      throw new Error(get(err, 'message') || err);
    }
    // TODO vuetify
    // dispatch('fetchClusterSchemes');
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }

  async function getClusterSchemeData(clusterScheme) {
    const { _id } = clusterScheme;
    const url = `/api/reference/reference-cluster-scheme/${_id}`;

    isLoadingClusterInformation.value = true;
    const [err, response] = await to(axios.get(url));
    isLoadingClusterInformation.value = false;
    if (err) throw new Error(err.message);
    clusterSchemeInformation.value = response.data;
  }

  async function processCSV({ fileId, mappings, delimiter }) {
    loading.value = true;
    const body = { mappings, delimiter };
    const [err, { data: responseData } = {}] = await to(
      axios.post(`/api/csv/reference-clustering/process/${fileId}`, body)
    );
    loading.value = false;
    // TODO: Remove this and use global interceptor on AOV3-159
    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', []));
      snackbarStore.showError(message);
      return Promise.reject(message);
    }

    if (size(responseData.messages)) {
      alertsStore.showMessages(responseData.messages);
      return {}; // AOV3-196 NOTE: should not upload anything if there are any errors.
    }

    return responseData.storeToClusterMappings;
  }

  async function setValidClusterSchemes() {
    if (!storeKeys.value) {
      // TODO vuetify
      // await dispatch('toolData/getStores', { root: true });
      stores.value = toolDataStore.stores;
      storeKeys.value = new Set(stores.value.map(c => c.storeKey));
    }

    // get the stores assigned, if not the same as all stores, then invalid
    forEach(clusterSchemes.value, scheme => {
      let valid = true;
      if (!scheme.clusters || !scheme.clusterCount || scheme.clusterCount < 2) {
        valid = false;
      }
      if (valid && some(scheme.clusters, 'isNoDataCluster')) {
        valid = false;
      }
      const assignedStoreKeys = [];
      scheme.clusters.forEach(c => {
        assignedStoreKeys.push(...c.storeKeys);
      });
      const currentStoreKeys = new Set(assignedStoreKeys);
      if (valid && !isEqual(currentStoreKeys, storeKeys.value)) {
        valid = false;
      }
      if (valid) {
        validClusterSchemes.value.push(scheme);
      }
    });
  }

  function setSelectedScheme(selectedSchemeRaw) {
    selectedScheme.value = selectedSchemeRaw;
  }

  function setSelectableClusterNames(newClusterNames) {
    selectableClusterNames.value = [...newClusterNames, i18n.t('clusteringPage.newCluster')];
  }
  function setSelectedSchemeClusters(clusters) {
    selectedScheme.value.clusters = clusters;
  }

  return {
    loading,
    clusterSchemes,
    validClusterSchemes,
    clusterSchemeInformation,
    isLoadingClusterInformation,
    selectedScheme,
    selectableClusterNames,
    stores,
    storeKeys,
    fetchClusterScheme,
    setSelectableClusterNames,
    setSelectedSchemeClusters,
    updateClusters,
    updateClusterSchemeName,
    fetchClusterSchemes,
    deleteClusterScheme,
    saveClusteringSchemes,
    getClusterSchemeData,
    processCSV,
    setValidClusterSchemes,
    setSelectedScheme,
    getClusterSchemes,
    // Editable table
    // States
    localDataGetterName,
    localDataChangesPerCell,
    localTableRowContents,
    localInvalidErrors,
    // Getters
    getTableRows,
    hasInvalidValuesInAnyCell,
    userMadeChangesToAnyCell,
    hasDataIn,
    hasChangesInCell,
    hasInvalidDataInCell,
    // Actions
    setNameOfLocalGetter,
    setTableRows,
    setInputValue,
    setInputError,
    setCellChanges,
    unloadData,
    markAllRowsAsChanged,
    initialiseEditableTable,
    onValueChanged,
    setUploadedData,
  };
});
